import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { SafetyPortfolioResponse } from '../../services/reportsApi';
import { listMemberships, type RbacMembership } from '../../services/rbacAdminApi';
import {
  buildDeniedSiteGovernanceRows,
  buildOrgMembershipScopeSummaries,
  buildPortfolioGovernanceSummary,
  portfolioGovernanceEmptyMessage,
} from '../../domain/safetyCases/workspacePortfolioGovernance';

function accessStateChipColor(accessState: string): 'success' | 'warning' | 'default' {
  if (accessState === 'loaded') return 'success';
  if (accessState === 'permission_denied' || accessState === 'unavailable') return 'warning';
  return 'default';
}

export function WorkspacePortfolioGovernancePanel({
  portfolio,
  loading,
  canManageAccessScope,
}: {
  portfolio?: SafetyPortfolioResponse | null;
  loading?: boolean;
  canManageAccessScope: boolean;
}) {
  const summary = useMemo(
    () => (portfolio ? buildPortfolioGovernanceSummary(portfolio) : null),
    [portfolio],
  );
  const deniedRows = useMemo(
    () => (portfolio ? buildDeniedSiteGovernanceRows(portfolio) : []),
    [portfolio],
  );
  const emptyMessage = portfolioGovernanceEmptyMessage(summary);
  const [memberships, setMemberships] = useState<RbacMembership[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipsError, setMembershipsError] = useState<string | null>(null);

  useEffect(() => {
    if (!canManageAccessScope || !summary?.orgId) {
      setMemberships([]);
      setMembershipsError(null);
      setMembershipsLoading(false);
      return;
    }
    let cancelled = false;
    setMembershipsLoading(true);
    setMembershipsError(null);
    void listMemberships({ org_id: summary.orgId })
      .then((rows) => {
        if (cancelled) return;
        setMemberships(rows);
      })
      .catch((error: { response?: { data?: { detail?: string; error?: string } }; message?: string }) => {
        if (cancelled) return;
        setMemberships([]);
        setMembershipsError(
          error?.response?.data?.detail
            || error?.response?.data?.error
            || error?.message
            || 'Failed to load org memberships',
        );
      })
      .finally(() => {
        if (!cancelled) setMembershipsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canManageAccessScope, summary?.orgId]);

  const membershipSummaries = useMemo(
    () => buildOrgMembershipScopeSummaries(memberships, portfolio?.site_coverage),
    [memberships, portfolio?.site_coverage],
  );

  const accessScopeHref = summary?.orgId
    ? `/admin/access-scope?org_id=${encodeURIComponent(summary.orgId)}`
    : '/admin/access-scope';

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="workspace-portfolio-governance">
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Portfolio access governance
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid="workspace-portfolio-governance-copy">
            Read-only access and coverage status for operating this org portfolio. This is not a risk score, ranking, or safety-performance conclusion.
          </Typography>
        </Box>

        {loading ? (
          <Skeleton variant="rounded" height={120} />
        ) : emptyMessage ? (
          <Alert severity="info">{emptyMessage}</Alert>
        ) : summary ? (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                color="success"
                variant="outlined"
                label={`${summary.authorizedCount} authorized site${summary.authorizedCount === 1 ? '' : 's'}`}
                data-testid="workspace-portfolio-governance-authorized"
              />
              <Chip
                color="default"
                variant="outlined"
                label={`${summary.loadedCount} loaded`}
                data-testid="workspace-portfolio-governance-loaded"
              />
              <Chip
                color={summary.deniedCount > 0 ? 'warning' : 'default'}
                variant="outlined"
                label={`${summary.deniedCount} denied`}
                data-testid="workspace-portfolio-governance-denied"
              />
              <Chip
                color={summary.unavailableCount > 0 ? 'warning' : 'default'}
                variant="outlined"
                label={`${summary.unavailableCount} unavailable`}
                data-testid="workspace-portfolio-governance-unavailable"
              />
              {summary.isPartial ? (
                <Chip color="warning" label="Partial portfolio access" data-testid="workspace-portfolio-governance-partial" />
              ) : (
                <Chip color="success" variant="outlined" label="Full portfolio access for your account" />
              )}
            </Stack>

            {summary.isPartial && summary.partialReasons.length > 0 ? (
              <Alert severity="warning" data-testid="workspace-portfolio-governance-partial-reasons">
                {summary.partialReasons.join(' ')}
              </Alert>
            ) : null}

            <TableContainer>
              <Table size="small" aria-label="Portfolio site access governance">
                <TableHead>
                  <TableRow>
                    <TableCell>Site</TableCell>
                    <TableCell>Access state</TableCell>
                    <TableCell>Reason / detail</TableCell>
                    <TableCell>Included in your portfolio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.siteAccessRows.map((row) => (
                    <TableRow key={row.siteId || row.displayName} data-testid={`workspace-portfolio-governance-row-${row.siteId || 'unknown'}`}>
                      <TableCell>{row.displayName}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={accessStateChipColor(row.accessState)}
                          label={row.accessStateLabel}
                        />
                      </TableCell>
                      <TableCell>{row.reasonDetail || '—'}</TableCell>
                      <TableCell>{row.includedInPortfolio ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {deniedRows.length > 0 ? (
              <Alert severity="warning" data-testid="workspace-portfolio-governance-denied-note">
                {deniedRows.length} site{deniedRows.length === 1 ? '' : 's'} are permission denied for your account and are excluded from Attention counts.
              </Alert>
            ) : null}
          </>
        ) : null}

        {canManageAccessScope ? (
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Org membership scope (read-only)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Membership rows configured for this organization. Edit access in Access Scope — this panel does not change RBAC.
              </Typography>
            </Box>
            {membershipsLoading ? (
              <Skeleton variant="rounded" height={72} />
            ) : membershipsError ? (
              <Alert severity="warning" data-testid="workspace-portfolio-governance-memberships-error">
                {membershipsError}
              </Alert>
            ) : membershipSummaries.length === 0 ? (
              <Alert severity="info" data-testid="workspace-portfolio-governance-memberships-empty">
                No org memberships were returned for this organization.
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small" aria-label="Org membership scope summary">
                  <TableHead>
                    <TableRow>
                      <TableCell>Scope</TableCell>
                      <TableCell>Membership rows</TableCell>
                      <TableCell>Roles</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {membershipSummaries.map((entry) => (
                      <TableRow key={entry.scopeKey} data-testid={`workspace-portfolio-governance-membership-${entry.scopeKey}`}>
                        <TableCell>{entry.siteLabel}</TableCell>
                        <TableCell>{entry.membershipCount}</TableCell>
                        <TableCell>{entry.roles.join(', ') || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box>
              <Button
                component={RouterLink}
                to={accessScopeHref}
                variant="outlined"
                size="small"
                data-testid="workspace-portfolio-governance-manage-access"
              >
                Manage access scope
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Opens the existing Access Scope admin workspace for membership assignment and conflict review.
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Contact an org admin with Access Scope permissions if you need additional site visibility.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
